import { Box, CardBody, Meter, MeterProps, Stack, Text } from 'grommet';

import { Estimate, EstimateStatus, PerformerContractStatus, Project } from '@talon/web/lib/api';
import { Delete, Divider, FavoriteButton, TalonCard } from '@talon/web/src/components/ui';
import { useProjectStore } from '@talon/web/src/context';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { borderRadiusSize } from 'src/theme';
import { shortenCurrency } from 'src/utils/currency';
import { formatDateOnly } from 'src/utils/dateOnly';
import { getAnimProps } from 'src/utils/styling';

export type ProjectCardProps = {
  project: Project;
  onClick: () => void;
};

export const ProjectCard = observer(({ project, onClick }: ProjectCardProps) => {
  /** Context **/
  const projectStore = useProjectStore();
  // const userStore = useUserStore();

  /** State **/

  // const [favs, setFavs] = useState<string[]>([]);
  const [meterLabel, setMeterLabel] = useState<string>('');
  const [finalEstimate, setFinalEstimate] = useState<Estimate | null>(null);
  const [firstSessionDate, setFirstSessionDate] = useState<string | null>(null);
  const [roleCount, setRoleCount] = useState<number>(0);
  const [castCount, setCastCount] = useState<number>(0);
  const [, setContractSignedCount] = useState<number>(0);
  const [castMeterLabel, setCastMeterLabel] = useState<string>('');

  /** Computed **/

  const meterValues: MeterProps['values'] = [
    {
      value: finalEstimate
        ? project.talentBudgetInPennies / 100 - finalEstimate.total_plus_fringe
        : project.talentBudgetInPennies / 100,
      color: 'brand',
      onHover: (over: unknown) =>
        over && finalEstimate
          ? setMeterLabel(
            `Remaining\n ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(project.talentBudgetInPennies / 100 - finalEstimate.total_plus_fringe || 0)}`
          )
          : setMeterLabel(''),
    },
    {
      value: finalEstimate?.total || 0,
      onHover: (over: unknown) =>
        over && finalEstimate
          ? setMeterLabel(
            `Estimated Cost\n ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(finalEstimate.total)}`
          )
          : setMeterLabel(''),
    },
    {
      value: finalEstimate ? finalEstimate.total_plus_fringe - finalEstimate.total : 0,
      onHover: (over: unknown) =>
        over && finalEstimate
          ? setMeterLabel(
            `Estimated Fringe\n ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(finalEstimate.total_plus_fringe - finalEstimate.total || 0)}`
          )
          : setMeterLabel(''),
    },
  ];

  /** Methods **/

  const deleteProject = async () => {
    if (!project) {
      toast.error('An internal error has occurred. Please refresh the page and try again.');
      return;
    }
    projectStore.deleteProject(project.id);
  };

  /** Effects **/

  useEffect(() => {
    project.estimates.find((estimate) => {
      if (estimate.status === EstimateStatus.Final) {
        setFinalEstimate(estimate);
      }
    });
    const session = [...project.sessions];
    if (session.length) {
      session.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setFirstSessionDate(formatDateOnly(session[0]?.date) || null);
    }
    setRoleCount(project.roles.length);
    setCastCount(project.contracts.length);
    setContractSignedCount(
      project.contracts.filter((c) => c.status === PerformerContractStatus.PerformerSigned).length
    );
  }, [project.id, projectStore]);

  // useEffect(() => {
  //   if (!userStore.user.favorite_project_ids) return;
  //   setFavs(userStore.user.favorite_project_ids);
  // }, [userStore.user.favorite_project_ids]);

  /** Render **/
  return (
    <TalonCard
      label={<Text margin={{ left: 'small' }}>{`${project.name} (${project.job_code})`}</Text>}
      buttons={[<Delete key="delete" onClick={() => deleteProject()} name={project.name} hoverIndicator />]}
      headerChildren={<FavoriteButton project={project} />}
      footerBackground="light-2"
      cardProps={{
        flex: { shrink: 1 },
      }}
      onHeaderClick={onClick}
    >
      <CardBody background="light-1" pad="medium" gap="medium" animation={getAnimProps(0, 'fadeIn')}>
        <Box direction="row" gap="medium" justify="between" align="center">
          {/* Project Details */}
          <Box direction="column" align="start" justify="between" gap="small" width="50%">
            <Box gap="xxsmall" fill="horizontal">
              <Text size="xsmall">ADVERTISER</Text>
              <Divider animation={getAnimProps(0, 'slideRight')} />
              <Text size="1rem" weight={600}>
                {project.client?.name}
              </Text>
            </Box>
            <Box gap="xxsmall" fill="horizontal">
              <Text size="xsmall">PRODUCT</Text>
              <Divider animation={getAnimProps(50, 'slideRight')} />
              <Text size="1rem" weight={600}>
                {project.product}
              </Text>
            </Box>
            {!project.is_union && (
              <Box gap="xxsmall" fill="horizontal">
                <Text size="xsmall">UNION</Text>
                <Divider animation={getAnimProps(100, 'slideRight')} />
                <Text size="1rem" weight={600}>
                  SAG-AFTRA
                </Text>
              </Box>
            )}
            <Box gap="xxsmall" fill="horizontal">
              <Text size="xsmall">SHOOT DATE</Text>
              <Divider animation={getAnimProps(150, 'slideRight')} />
              <Box height="1.2rem">
                <Text size="1rem" weight={firstSessionDate ? 600 : 300}>
                  {firstSessionDate || '-'}
                </Text>
              </Box>
            </Box>
          </Box>

          {/* Budget Meter */}
          <Box direction="column" align="center" width="50%">
            <Stack anchor="center">
              <Meter
                type="circle"
                size="small"
                background={{ color: 'white' }}
                values={meterValues}
                max={project.talentBudgetInPennies / 100 || 0}
              />

              <Box align="center" pad="xsmall" gap="xsmall">
                <Text size="xsmall" textAlign="center">
                  {(meterLabel || 'Talent Budget').toLocaleUpperCase()}
                </Text>
                <Divider />
                <Text size="1rem" textAlign="center" weight={600}>
                  {shortenCurrency(project.talentBudgetInPennies / 100 || 0, { hideZeroDecimals: true })}
                  {/* {`${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(project.talentBudgetInPennies / 100 || 0)}`} */}
                </Text>
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Roles Overview */}
        {roleCount && (
          <Box direction="column" gap="small" justify="center" align="center" margin={{ top: 'xsmall' }}>
            <Stack anchor="center">
              <Box fill="horizontal" round="24px" elevation="xsmall" animation={getAnimProps(0, 'slideUp', 400)}>
                <Meter
                  type="bar"
                  round
                  size="medium"
                  values={[
                    {
                      value: castCount || 0,
                      color: 'status-ok',
                      onHover: (over) => {
                        over && castCount ? setCastMeterLabel(`Cast: ${castCount}`) : setCastMeterLabel('');
                      },
                    },
                    // {
                    //   value: (castCount - contractSignedCount) || 0,
                    //   onHover: (over) => {
                    //     over && contractSignedCount ? setCastMeterLabel(`Contract Signed: ${contractSignedCount}`) : setCastMeterLabel('');
                    //   },
                    // },
                    {
                      value: roleCount - castCount || 0,
                      color: 'white',
                      onHover: (over) => {
                        over && roleCount !== castCount
                          ? setCastMeterLabel(`To be cast: ${roleCount - castCount}`)
                          : setCastMeterLabel('');
                      },
                    },
                  ]}
                  max={roleCount || 0}
                  thickness="26px"
                  direction="horizontal"
                />
              </Box>
              <Box
                background={{ color: 'brand-gradient', dark: true }}
                style={{ background: 'var(--brand-gradient)' }}
                justify="start"
                align="center"
                round={borderRadiusSize}
                pad={{ horizontal: 'small', vertical: '0.6rem' }}
                elevation="small"
              >
                <Text size="small">{castMeterLabel || `${roleCount} Roles`}</Text>
              </Box>
            </Stack>
          </Box>
        )}
      </CardBody>
    </TalonCard>
  );
});
